import React, { useState, useEffect } from "react";
import Table from "../components/Table/Table";
import TopNavbar from "../components/TopNavbar/TopNavbar";
import ApiManager from "../ApiManager/ApiManager";
import { useNavigate } from "react-router-dom";
import Modal from "../components/Modal/Modal";
import ViewEntity from "../components/ViewEntity/ViewEntity";
import FinalInvoice from "./FinalInvoice/FinalInvoice";
import { useAuth } from "../components/AuthProvider";
import PERMISSIONS from "../permissions";
import { hasPermission } from "../components/RoleBasedComponents";
import { useComponentLoading } from "../utils/useComponentLoading";

function Invoices() {
  const { userPermissions } = useAuth();
  const { withLoading, isLoading, getMessage, getProgress } = useComponentLoading();

  const [data, setData] = useState([]);
  const [invoice, setInvoice] = useState([]);
  const [rowId, setRowId] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isDetails, setDetails] = useState(false);
  const [isEdit, setEdit] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  const fetchData = async () => {
    try {
      await withLoading('fetchInvoices', async (onProgress) => {
        onProgress(10, 'Fetching invoices...');
        const params = new URLSearchParams({
          PageSize: 12,
          PageNumber: currentPage,
        });
        const response = await ApiManager.getAllInvoices(params);
        onProgress(50, 'Processing data...');
        if (!response.ok) throw new Error("Failed to fetch invoices");
        const json = await response.json();
        setData(json.invoices || []);
        setHasMorePages(json.length == 12);
        onProgress(100, 'Completed');
      });
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setHasMorePages(false);
    }
  };

  const navigate = useNavigate();

  const handleClick = (path) => {
    navigate(path);
  };

  const toggleModal = () => {
    setModalOpen(!isModalOpen);
  };

  const handleEdit = () => {
    console.log("Edit clicked for row:");
    setDetails(false);
    setEdit(true);
    setModalOpen(true);
  };

  const handleDetails = () => {
    console.log("Details clicked for row:");
    setDetails(true);
    setEdit(false);
    setModalOpen(true);
  };

  const handleFileClick = async (fileName) => {
    try {
      await withLoading('downloadInvoice', async (onProgress) => {
        onProgress(20, 'Downloading invoice...');
        const query = new URLSearchParams({ file_name: fileName });
        const response = await ApiManager.getFile(query);

        if (!response.ok) {
          throw new Error("Failed to fetch file");
        }

        onProgress(60, 'Processing file...');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "invoice.xls";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        onProgress(100, 'Download complete');
      });
    } catch (error) {
      console.error("Error fetching file:", error);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setDetails(false);
  };

  const handleRowClick = (row) => {
    setRowId(row);
  };

  const buttons = [{ label: "Details", onClick: () => handleDetails() }];

  if (hasPermission(userPermissions, PERMISSIONS.VENDOR_INVOICE_READ)) {
    buttons.push({
      label: "Download Excel",
      onClick: () => handleFileClick(invoice.excel),
    });
  }

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        await withLoading('fetchInvoiceDetails', async (onProgress) => {
          onProgress(30, 'Fetching invoice details...');
          const query = new URLSearchParams({
            InvoiceNo: rowId,
          });
          const response = await ApiManager.getInvoice(query);
          if (!response.ok) throw new Error("Failed to fetch invoice");
          onProgress(70, 'Processing invoice data...');
          const result = await response.json();
          setInvoice(result);
          onProgress(100, 'Completed');
        });
      } catch (error) {
        console.error("Error fetching invoice:", error);
      }
    };
    if (rowId) fetchInvoice();
  }, [rowId]);

  const table_columns = [
    { field: "invoiceNumber", headerName: "Invoice Number" },
    { field: "companyName", headerName: "Company" },
    { field: "tripField", headerName: "Trips" },
    { field: "totalAmount", headerName: "Total Amount" },
  ];

  const view_columns = [
    { field: "invoiceNumber", headerName: "Invoice Number" },
    { field: "companyName", headerName: "Company" },
    { field: "tripField", headerName: "Trips" },
    { field: "totalAmount", headerName: "Total Amount" },
  ];

  //   console.log("data: "+data);
  const rows = (data || []).map((item) => ({
    id: item.invoiceNumber,
    invoiceNumber: item.invoiceNumber,
    companyName: item.companyName,
    tripField: item.tripField,
    totalAmount: item.totalAmount,
  }));

  return (
    <div>
      <TopNavbar
        title="Invoices"
      />
      <Table
        table_columns={table_columns}
        rows={rows}
        setRowsId={setRowId}
        buttons={buttons}
        onRowClick={handleRowClick}
        toggleModel={toggleModal}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        hasMorePages={hasMorePages}
        isLoading={isLoading('fetchInvoices')}
        loadingMessage={getMessage('fetchInvoices')}
        loadingProgress={getProgress('fetchInvoices')}
      />
      {isModalOpen && isDetails && (
          <Modal isOpen={true} onClose={handleModalClose} title={`Invoice: ${invoice.invoiceNumber}`}>
            <FinalInvoice
              invoiceNumber={invoice.invoiceNumber}
              finalHtml={invoice.html}
              isLoading={isLoading('fetchInvoiceDetails')}
              loadingMessage={getMessage('fetchInvoiceDetails')}
              loadingProgress={getProgress('fetchInvoiceDetails')}
            />
          </Modal>
      )}
      {isModalOpen && rowId && (
        <div className="details-container">
          <Modal isOpen={true} onClose={handleModalClose}>
            <ViewEntity
              id={rowId}
              fetchFunction={ApiManager.getInvoice}
              columns={view_columns}
              entity="InvoiceNo"
              isLoading={isLoading('fetchInvoiceDetails')}
              loadingMessage={getMessage('fetchInvoiceDetails')}
              loadingProgress={getProgress('fetchInvoiceDetails')}
            />
          </Modal>
        </div>
      )}
    </div>
  );
}

export default Invoices;
